import { createFileRoute, Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { AppNav } from "@/components/AppNav";
import { useEffect, useState } from "react";
import { useSession } from "@/hooks/useSession";
import { supabase, API_URL } from "@/lib/supabase";
import { posthog } from "@/lib/posthog";

const PLAN_INFO = {
  pro: {
    name: "Pro",
    price: "R$ 29/mês",
    sub: "Cancele quando quiser",
  },
} as const;

export const Route = createFileRoute("/upgrade")({
  validateSearch: (search: Record<string, unknown>) => ({
    plan: (search.plan as "pro" | undefined) ?? "pro",
  }),
  component: UpgradePage,
});

function UpgradePage() {
  const { pathname } = useRouterState({ select: (s) => s.location });
  // upgrade.success is a child route of upgrade in TanStack Router file-based
  // routing (dot = nesting). When on any /upgrade/* child, render the outlet
  // so the child component shows instead of this form running its effects.
  // Strip a trailing slash first so the form URL itself — /upgrade/ under
  // trailingSlash:'always' — isn't mistaken for a child route.
  if (pathname.replace(/\/$/, "") !== "/upgrade") return <Outlet />;

  const { plan } = Route.useSearch();
  const { session, loading: sessionLoading } = useSession();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [cpf, setCpf] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [autoRedirecting, setAutoRedirecting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (sessionLoading || !session) return;

    supabase
      .from("profiles")
      .select("full_name, cpf, tier, plan_expires_at")
      .single()
      .then(({ data }) => {
        const returnedFromStripe = sessionStorage.getItem("stripe_checkout_pending") === "1";
        sessionStorage.removeItem("stripe_checkout_pending");

        if (data?.full_name && data?.cpf && !returnedFromStripe) {
          setAutoRedirecting(true);
          callCheckout(data.full_name, data.cpf).catch((err: unknown) => {
            setError(err instanceof Error ? err.message : "Algo deu errado. Tente novamente.");
            setName(data.full_name);
            setAutoRedirecting(false);
            setShowForm(true);
          });
        } else {
          if (data?.full_name) setName(data.full_name);
          setShowForm(true);
        }
      });
  }, [session, sessionLoading]);

  if (!sessionLoading && !session) {
    navigate({ to: "/login", search: { next: `/upgrade?plan=${plan}` } });
    return null;
  }

  const info = PLAN_INFO[plan as keyof typeof PLAN_INFO] ?? PLAN_INFO.pro;

  const callCheckout = async (nameVal: string, cpfVal: string) => {
    const { data: { session: fresh } } = await supabase.auth.getSession();
    const res = await fetch(`${API_URL}/api/checkout`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${fresh?.access_token}`,
      },
      body: JSON.stringify({ plan_id: plan, name: nameVal, cpf: cpfVal }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error ?? "Algo deu errado.");
    posthog.capture("upgrade_started", { plan_id: plan });
    sessionStorage.setItem("stripe_checkout_pending", "1");
    window.location.href = data.url;
  };

  const formatTaxId = (v: string) => {
    const d = v.replace(/\D/g, "").slice(0, 14);
    if (d.length <= 11) {
      return d
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    }
    return d
      .replace(/(\d{2})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1/$2")
      .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
  };

  const isValidTaxId = (raw: string): boolean => {
    const d = raw.replace(/\D/g, "");
    if (d.length === 11) {
      if (/^(\d)\1{10}$/.test(d)) return false;
      const calc = (len: number) => {
        const sum = d.slice(0, len).split("").reduce((acc, n, i) => acc + Number(n) * (len + 1 - i), 0);
        const r = sum % 11;
        return r < 2 ? 0 : 11 - r;
      };
      return calc(9) === Number(d[9]) && calc(10) === Number(d[10]);
    }
    if (d.length === 14) {
      if (/^(\d)\1{13}$/.test(d)) return false;
      const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
      const w2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
      const calc = (weights: number[]) => {
        const sum = weights.reduce((acc, w, i) => acc + Number(d[i]) * w, 0);
        const r = sum % 11;
        return r < 2 ? 0 : 11 - r;
      };
      return calc(w1) === Number(d[12]) && calc(w2) === Number(d[13]);
    }
    return false;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const rawTaxId = cpf.replace(/\D/g, "");
    if (!isValidTaxId(rawTaxId)) {
      setError("CPF ou CNPJ inválido.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await callCheckout(name, rawTaxId);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Falha de conexão. Tente novamente.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen text-foreground">
      <AppNav maxWidth="max-w-5xl" />

      <main className="mx-auto max-w-md px-6 pb-32 pt-10">
        <div className="surface-card rounded-3xl px-8 py-10">
          <div className="mb-6 text-center">
            <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-primary">
              {info.name}
            </p>
            <p className="mt-1 font-display text-3xl text-foreground">{info.price}</p>
            <p className="text-sm text-muted-foreground">{info.sub}</p>
            <p className="mt-2 text-xs text-muted-foreground">
              Assinatura mensal · cobrança recorrente · cancele quando quiser
            </p>
          </div>

          <div className="my-6 border-t dark:border-white/10 border-black/10" />

          {autoRedirecting && (
            <div className="flex flex-col items-center gap-4 py-6">
              <div className="h-8 w-8 animate-spin rounded-full border-2 dark:border-white/10 border-black/10 border-t-primary" />
              <p className="text-sm text-muted-foreground">Redirecionando para o Stripe…</p>
            </div>
          )}

          {showForm && (
            <>
              <p className="mb-5 text-sm text-muted-foreground">
                Para emissão da cobrança, precisamos dos seus dados. O cartão você preenche com
                segurança no ambiente do Stripe.
              </p>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-xs text-muted-foreground">
                    Nome completo
                  </label>
                  <input
                    type="text"
                    autoComplete="name"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Seu nome completo"
                    className="w-full rounded-lg border dark:border-white/10 border-black/10 dark:bg-white/[0.03] bg-black/[0.03] px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none transition focus:border-primary/60"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs text-muted-foreground">CPF ou CNPJ</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    required
                    value={cpf}
                    onChange={(e) => setCpf(formatTaxId(e.target.value))}
                    placeholder="CPF ou CNPJ"
                    className="w-full rounded-lg border dark:border-white/10 border-black/10 dark:bg-white/[0.03] bg-black/[0.03] px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none transition focus:border-primary/60"
                  />
                </div>

                {error && <p className="text-xs text-red-400">{error}</p>}

                <button
                  type="submit"
                  disabled={loading}
                  className="mt-2 w-full rounded-full bg-primary px-5 py-3 text-sm font-medium text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
                >
                  {loading ? "Aguarde…" : "Continuar para pagamento →"}
                </button>
              </form>
            </>
          )}

          {!autoRedirecting && !showForm && !error && (
            <div className="flex justify-center py-6">
              <div className="h-6 w-6 animate-spin rounded-full border-2 dark:border-white/10 border-black/10 border-t-primary" />
            </div>
          )}

          <p className="mt-5 text-center text-xs text-muted-foreground">
            Cancele direto pelo painel · sem perguntas
          </p>
        </div>
      </main>
    </div>
  );
}
